/**
 * Helper functions for consistent cache UI across all stages
 */

/**
 * Get a human-readable cache status message
 * @param cachedData - The cached data object
 * @param isCheckingCache - Whether cache is currently being checked
 * @param flowId - The flow ID (null if no flow)
 * @returns A status message string
 */
export const getCacheStatusMessage = (
  cachedData: any,
  isCheckingCache: boolean,
  flowId: string | null | undefined
): string => {
  if (isCheckingCache) {
    return 'Checking cache...';
  }
  
  if (!flowId) {
    return 'No flow ID - cache not available';
  }
  
  if (cachedData && Object.keys(cachedData).length > 0) {
    // Try to get timestamp from cache if available
    const timestamp = cachedData.timestamp || cachedData.generatedAt || cachedData.createdAt;
    if (timestamp) {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffHours > 0) {
        return `cached ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else if (diffMinutes > 0) {
        return `cached ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
      } else {
        return 'cached less than a minute ago';
      }
    }
    return 'cached data available';
  }
  
  return 'no cache available';
};

/**
 * Get the appropriate button text based on cache state
 * @param baseText - The base button text (e.g., "Generate PSC Codes")
 * @param useCachedData - Whether cached data should be used
 * @param hasCachedData - Whether cached data exists
 * @returns The button text to display
 */
export const getButtonText = (
  baseText: string,
  useCachedData: boolean,
  hasCachedData: boolean
): string => {
  if (!useCachedData && hasCachedData) {
    // Replace "Generate" with "Regenerate" if not using cache and cache exists
    return baseText.replace('Generate', 'Regenerate').replace('Assign', 'Reassign').replace('Find', 'Re-find').replace('Map', 'Remap');
  }
  return baseText;
};